// UploadPdfReport.jsx — v161.22.29
//
// Alternative bug-report submission path: tester drafts a report in
// Word/Google Docs (or whatever), exports as PDF, uploads here. Backend
// extracts the text via pdf-parse, files a one-entry submitted report,
// and routes it through the same auto-fix pipeline as the structured
// form. PDF stays on disk so Claude can also read it natively if needed.
//
// Differences from NewReport.jsx:
//   • Single-step (no draft → editor → submit dance — uploads are already
//     final, so we submit immediately).
//   • PDF file picker instead of inline entry form.
//   • Shows a preview of the extracted page count + character count
//     after upload so the tester can confirm the PDF parsed correctly.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { me, bugReports } from '../lib/api.js';

const DEVICE_KEY = 'velkra_dist_device_model';
const MAX_PDF_MB = 20;

const DEVICE_MODELS = [
  'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
  'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
  'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 mini',
  'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 mini',
  'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
  'iPhone SE (3rd gen)', 'iPhone SE (2nd gen)',
  'iPad Pro 13"', 'iPad Pro 11"', 'iPad Air', 'iPad mini', 'iPad',
  'Other'
];

function detectIOSVersion() {
  const ua = navigator.userAgent || '';
  const m = ua.match(/OS (\d+)[._](\d+)(?:[._](\d+))?/i);
  if (!m) return '';
  return [m[1], m[2], m[3]].filter(Boolean).join('.');
}

export default function UploadPdfReport() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [appSlug, setAppSlug] = useState('');
  const [builds, setBuilds] = useState([]);
  const [buildId, setBuildId] = useState('');
  const [deviceModel, setDeviceModel] = useState(
    localStorage.getItem(DEVICE_KEY) || ''
  );
  const [iosVersion, setIosVersion] = useState(detectIOSVersion());
  const [title, setTitle] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    me.apps()
      .then(r => setApps(r.apps || []))
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    if (!appSlug) { setBuilds([]); setBuildId(''); return; }
    me.builds(appSlug)
      .then(r => {
        setBuilds(r.builds || []);
        if (r.builds?.length) setBuildId(r.builds[0].id);
      })
      .catch(e => setError(e.message));
  }, [appSlug]);

  function handleFile(file) {
    setError('');
    if (!file) { setPdfFile(null); setPdfBase64(null); return; }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file.');
      return;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      setError(`PDF too large — max ${MAX_PDF_MB} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).replace(/^data:[^;]+;base64,/, '');
      setPdfFile(file);
      setPdfBase64(b64);
      // Pre-fill title from filename if the field is empty.
      if (!title) {
        setTitle(file.name.replace(/\.pdf$/i, ''));
      }
    };
    reader.onerror = () => setError('Failed to read PDF file.');
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!appSlug) { setError('Pick an app first'); return; }
    if (!buildId) { setError('Pick a build first'); return; }
    if (!deviceModel) { setError('Pick your device'); return; }
    if (!pdfBase64) { setError('Attach a PDF first'); return; }

    setSubmitting(true);
    setError('');
    try {
      localStorage.setItem(DEVICE_KEY, deviceModel);
      const result = await bugReports.uploadPdf({
        appSlug, buildId,
        title: title.trim() || null,
        deviceModel, iosVersion,
        pdfBase64,
        pdfFilename: pdfFile?.name || null
      });

      // v161.22.30: same likely-duplicate short-circuit logic as the
      // structured form. If the matcher found a high-confidence match,
      // no fix request was queued; tell the tester explicitly.
      if (result.state === 'likely_duplicate') {
        const top = (result.possibleAlreadyFixed || []).slice(0, 3);
        const list = top.map(m =>
          `• "${m.bugTitle}" — fixed in build ${m.fixedBuildLabel} on ${(m.fixedAt || '').slice(0, 10)}`
        ).join('\n');
        alert(
          `Your PDF report looks like a bug we already fixed recently:\n\n${list}\n\n` +
          `Try installing the most recent build first. If the bug is still happening, ` +
          `open the report and tap "Run auto-fix anyway".`
        );
        navigate(`/reports/${result.reportId}`, { replace: true });
        return;
      }

      // The PDF upload is final (no draft phase) — go straight to the
      // read-only detail page. If the PDF extraction was thin (very few
      // chars), warn the tester so they know the auto-fix may struggle.
      if (result.extractedTextChars !== undefined && result.extractedTextChars < 100) {
        alert(
          `Submitted, but only ${result.extractedTextChars} characters of text were extracted from the PDF. ` +
          `If the PDF is mostly images (scanned pages, screenshots), Claude can still read it directly, ` +
          `but text-only fixes may not be possible. Consider also uploading screenshots or using the ` +
          `structured form for clearer reproduction steps.`
        );
      }
      // Soft warnings for low-confidence already-fixed matches.
      if (Array.isArray(result.possibleAlreadyFixed) && result.possibleAlreadyFixed.length > 0) {
        const fixList = result.possibleAlreadyFixed
          .slice(0, 3)
          .map(m => `• "${m.bugTitle}" (fixed in ${m.fixedBuildLabel})`)
          .join('\n');
        alert(
          `Submitted. Heads up: this report looks similar to bugs we've already fixed:\n\n${fixList}\n\n` +
          `Auto-fix will run anyway, but you may want to verify against the most recent build first.`
        );
      }
      navigate(`/reports/${result.reportId}`, { replace: true });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  if (apps === null) return <div className="loading">Loading apps…</div>;
  if (apps.length === 0) {
    return (
      <div className="empty-state">
        <h2>No apps available to test</h2>
        <p>You don't have access to any apps yet. Ask the developer to add you to a tester group.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>Upload PDF Bug Report</h1>
      </header>

      <div className="card" style={{ background: 'rgba(108,92,231,0.08)', fontSize: 13, marginBottom: 20 }}>
        📄 Drafted your report in Word or Google Docs? Upload the PDF here.
        We'll extract the text and route it through the same auto-fix pipeline.
        Max {MAX_PDF_MB} MB. The PDF is also retained so Claude can read it
        directly if needed.
      </div>

      <form className="form" onSubmit={handleSubmit}>
        <div className="field">
          <label>App</label>
          <select value={appSlug} onChange={e => setAppSlug(e.target.value)} required>
            <option value="">— Select app —</option>
            {apps.map(a => (
              <option key={a.app_slug} value={a.app_slug}>{a.app_name || a.app_slug}</option>
            ))}
          </select>
        </div>

        {appSlug && (
          <div className="field">
            <label>Build</label>
            {builds.length === 0 ? (
              <div className="muted">No builds installed for this app yet.</div>
            ) : (
              <select value={buildId} onChange={e => setBuildId(e.target.value)} required>
                {builds.map(b => (
                  <option key={b.id} value={b.id}>
                    v{b.version} ({b.build_number}) — {b.channel || 'testing'}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="field">
          <label>Device</label>
          <select value={deviceModel} onChange={e => setDeviceModel(e.target.value)} required>
            <option value="">— Select device —</option>
            {DEVICE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="field">
          <label>iOS version</label>
          <input
            type="text"
            value={iosVersion}
            onChange={e => setIosVersion(e.target.value)}
            placeholder="e.g. 18.3.1"
          />
        </div>

        <div className="field">
          <label>Report title (optional)</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Auto-fills from PDF filename if blank"
          />
        </div>

        <div className="field">
          <label>PDF file <span className="req">*</span></label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={e => handleFile(e.target.files?.[0])}
            required
          />
          {pdfFile && (
            <small className="muted" style={{ display: 'block', marginTop: 6 }}>
              ✓ {pdfFile.name} · {(pdfFile.size / 1024).toFixed(0)} KB
            </small>
          )}
          <small className="muted">Max {MAX_PDF_MB} MB. Text is auto-extracted; scanned/image-only PDFs still work but may need clearer follow-up.</small>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !pdfBase64}
          >
            {submitting ? 'Uploading…' : 'Upload and submit'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/reports')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
